<template>
  <div>
    <div
      class="dropzone-container"
      :class="{ 'is-dragging': isDragging, 'is-error' : this.errorMsg }"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <input
        type="file"
        multiple
        name="file"
        id="fileInput"
        class="hidden-input"
        @change="onChange"
        ref="file"
        :accept="allowedMimeTypes.join(',')"
      />

      <div class="text-center m-t-30 m-b-30">
        <div class="m-b-30">{{ $t('LBL_UPLOAD_DOCUMENTS') }}</div>
        <p class="upload-description-text m-b-30">{{ $t('TXT_SELECT_DOCUMENT_TYPE_AND_DRAG_AND_DROP_FILES_HERE') }}</p>
        <div class="upload-container">
          <div class="flex-grow">
            <InputSelect
              class="document-type-select"
              v-model="documentType"
              :options="documentOptions"
              @change="$emit('document-type-changed', $event)"
            />
          </div>
          <div>
            <label for="fileInput" class="file-label">
              <Btn
                class="brand"
                style="height: 45px;
    width: 206px;
    margin-left: 15px;"
                :icon="{
                  name: 'agreements',
                  size: '16',
                }"
                :btnText="$t('LBL_CHOOSE_FILES_FROM_PC')"
              />
            </label>
          </div>
        </div>
        <p class="upload-description-text m-t-30">
          {{ $t('LBL_SUPPORTED_FORMATS', {formats : 'PDF (.pdf)'}) }} -
          {{ $t('LBL_MAX_SIZE', {size : '4MB'}) }} -
          {{ $t('LBL_MULTIPLE_FILES_ALLOWED') }}
        </p>
      </div>

      <div class="preview-container mt-4" v-if="files && files.length">
        <div v-for="(file, index) in files" :key="file.fileName" class="preview-card">
          <div>
            <span>{{ file.typeText }}: </span>
            <span :title="file.fileName" class="uploaded-file-name">{{ makeName(file.fileName) }}</span>
          </div>
          <div>
            <Icon
              @click.native.stop="remove(index)"
              :circle="true"
              customClass="circled-icon-small"
              name="deleteForever"
              size="15"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import InputSelect from "../elements/inputs/select.vue";
import Btn from '@/components/elements/buttons/button.vue';

export default {
  name: 'document-uploader',
  components: {Btn, InputSelect },
  props: {
    allowedMimeTypes: {
      type: Array,
      default: () => ['application/pdf'], // Default to PDF if not specified
    },
    uploadedFiles: {
      type: Array,
      default: () => [], // Default to PDF if not specified
    },
    initialDocumentType: {
      type: String,
      default: 'CINV'
    },
    // DG document is offered only when this prop is set: in the standalone "always show upload" box,
    // and in the customs box for dangerous goods shipments. Ordinary declarable shipments don't get it.
    showDangerousGoods: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragging: false,
      files: [],
      documentType: 'CINV',
      customsDocuments: [
        { value: 'CINV', text: 'Commercial invoice' },
        { value: 'PF', text: 'Proforma' },
        { value: 'COO', text: 'Certificate of origin' },
        { value: 'PL', text: 'Packing list' },
        { value: 'EXPODEC', text: 'Export declaration' },
      ],
      errorMsg: '', // To display error message if invalid file type
    };
  },
  computed: {
    documentOptions() {
      const options = [...this.customsDocuments];

      if (this.showDangerousGoods) {
        options.push({ value: 'DNGGOODS', text: 'Dangerous goods document' });
      }

      return options;
    }
  },
  mounted() {
    this.files = this.uploadedFiles
    this.documentType = this.initialDocumentType
  },
  watch: {
    // Adopt externally provided files that arrive after mount (e.g. when re-opening a draft or
    // copying/returning a shipment, where the references load asynchronously). Only adopt while the
    // local list is empty, so we never clobber files the user just added.
    uploadedFiles(newVal) {
      if (Array.isArray(newVal) && newVal.length && (!this.files || this.files.length === 0)) {
        this.files = newVal;
      }
    }
  },
  methods: {
    async onChange() {
      const selectedFiles = Array.from(this.$refs.file.files);
      for (const file of selectedFiles) {
        if (this.validateMimeType(file) && this.validateFileSize(file)) {
          await this.processFile(file);
        }
      }
    },

    async processFile(file) {
      if (!this.files) {
        this.files = [];
      }
      // Check for duplicates
      if (this.files.some(existingFile => existingFile.fileName === file.name)) {
        return; // Skip duplicates
      }

      // Get base64 content
      const content = await this.toBase64(file);

      // Get selected document type
      const selectedDocument = this.documentOptions.find(doc => doc.value === this.documentType);

      // Create the file object
      const newFile = {
        content,
        typeId: selectedDocument.value,
        typeText: selectedDocument.text,
        fileName: file.name,
        fileType: file.type
      };

      // Append new file
      this.files = [...this.files, newFile];
      this.$emit('file-uploaded', this.files)
    },

    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },

    makeName(name) {
      return (
        name.split(".")[0].substring(0, 50) +
        "..." +
        name.split(".")[name.split(".").length - 1]
      );
    },

    remove(index) {
      this.files.splice(index, 1);
    },

    dragover(e) {
      e.preventDefault();
      this.isDragging = true;
    },

    dragleave() {
      this.isDragging = false;
    },

    async drop(e) {
      e.preventDefault();
      const droppedFiles = Array.from(e.dataTransfer.files);
      for (const file of droppedFiles) {
        if (this.validateMimeType(file) && this.validateFileSize(file)) {
          await this.processFile(file);
        }
      }
      this.isDragging = false;
    },

    validateMimeType(file) {
      if (this.allowedMimeTypes.includes(file.type)) {
        this.errorMsg = '';
        return true;
      } else {
        this.errorMsg = this.$t('LBL_FILE_TYPE_NOT_ALLOWED')
        this.$store.commit('SHOW_NOTIFICATION_BAR', {
          message: this.errorMsg,
          type: 'alert-window'
        });
        console.error(this.errorMsg);
        setTimeout(() => this.errorMsg = '', 3000)
        return false;
      }
    },
    validateFileSize(file) {
      const maxSizeMB = 4;
      const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes

      if (file.size <= maxSizeBytes) {
        return true;
      } else {
        this.errorMsg = this.$t('LBL_FILE_SIZE_EXCEEDED', { size: maxSizeMB });
        this.$store.commit('SHOW_NOTIFICATION_BAR', {
          message: this.errorMsg,
          type: 'alert-window'
        });
        setTimeout(() => this.errorMsg = '', 3000);
        return false;
      }
    }
  }
};
</script>

<style>
.dropzone-container {
  padding: 15px;
  border-radius: 4px;
  background: #fff;
  width: calc(100% + 8px);
  margin-left: -4px;
  border: 2px dotted #DBDFE3;
}

.dropzone-container.is-dragging {
  background: #f9f9f9;
  border: 2px dotted #222;
}

.dropzone-container.is-error {
  background: #ffdddd;
  border: 2px dotted #ea4f4f;
}

.uploaded-file-name {
  color: #949397;
}

.upload-description-text {
  color: #989FA6;
  font-size: 14px;
}

.document-type-select {
  width: 200px;
  text-align: left;
}

.hidden-input {
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 1px;
  height: 1px;
}

.file-label {
  font-size: 20px;
  display: block;
  cursor: pointer;
}

.upload-container {
  display: flex;
  justify-content: center;
  width: 230px;
  margin: 0 auto;
}

.flex-grow {
  display: flex;
  flex-grow: 1;
}

.preview-container {
  margin-top: 2rem;
}


.preview-card {
  display: flex;
  justify-content: space-between;
  background: #F3F4F7;
  padding: 10px 10px 10px 15px;
  border-radius: 4px;
  margin-bottom: 4px;
  align-content: center;
  flex-wrap: wrap;
  font-size: 14px;
  align-items: center;
}

.preview-card:last-child {
  margin-bottom: 0;
}


.preview-img {
  width: 50px;
  height: 50px;
  border-radius: 5px;
  border: 1px solid #a2a2a2;
  background-color: #a2a2a2;
}
</style>
